/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Activity, AlertTriangle, ArrowRight, ArrowLeft, Bot, Clock, Database,
  HardDrive, Inbox, MessageSquare, RefreshCw, Send, Shield, ShieldCheck,
  Sparkles, Timer, Upload, Users, Zap, CircleDot,
} from 'lucide-react';

/**
 * Architecture Map (admin-only)
 * ─────────────────────────────
 * A live, interactive visual of VawworldOS's Two-Way Highway:
 *   1. Forward lane — Artist → Supervisor → Producer → Client
 *   2. Reverse lane — Client revise/reject cascade
 *   3. Deadline rail — every active task bucketed by hours-to-deadline
 *   4. Bot orbit — 11 autonomous watchdogs ringing the data core
 *   5. Communication ledger — last 25 timestamped events
 *
 * All data refreshes from /api/architecture/live every 20 seconds.
 */
export default function ArchitectureMap() {
  const { api, user } = useAuth();
  const [snap, setSnap] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get('/architecture/live');
      setSnap(data); setError(''); setLoading(false);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load snapshot');
      setLoading(false);
    }
  };
  useEffect(() => { load(); const iv = setInterval(load, 20000); return () => clearInterval(iv); }, []);

  if (loading) return <div className="p-10 text-zinc-500 text-sm">Loading live architecture…</div>;
  if (error)   return <div className="p-10 text-rose-300 text-sm">{error}</div>;
  if (!snap)   return null;

  const { lanes, deadlines, bots, ledger, totals } = snap;
  const totalDeadline = deadlines.reduce((a, b) => a + b.count, 0);
  const overdue = deadlines.find(d => d.bucket === 'overdue')?.count || 0;
  const critical = deadlines.find(d => d.bucket === 'lt_24h')?.count || 0;

  return (
    <div className="p-6 space-y-6 bg-zinc-950 min-h-screen text-white" data-testid="architecture-map">
      {/* ── Header ── */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-amber-300 via-rose-300 to-violet-300 bg-clip-text text-transparent">
            VawworldOS Architecture · Live Two-Way Highway
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Snapshot @ <span className="font-mono text-zinc-300">{fmtTs(snap.snapshot_at)}</span> · auto-refresh every 20s · admin-only
          </p>
        </div>
        <div className="flex gap-3">
          <Pill icon={<Activity className="h-3 w-3" />} label="Active tasks" value={totals.active_tasks} color="violet" />
          <Pill icon={<Inbox className="h-3 w-3" />}    label="On review"    value={totals.on_review}    color="amber" />
          <Pill icon={<ShieldCheck className="h-3 w-3" />} label="Approved today" value={totals.approved_today} color="emerald" />
          <button onClick={load} className="h-8 px-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs rounded flex items-center gap-1.5" data-testid="arch-refresh"><RefreshCw className="h-3 w-3"/>Refresh</button>
        </div>
      </div>

      {/* ── Manifesto strip ── */}
      <div className="rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-rose-500/5 to-transparent px-5 py-3 flex items-center gap-3">
        <Timer className="h-5 w-5 text-amber-300 shrink-0"/>
        <div className="flex-1">
          <p className="text-xs text-amber-200 font-semibold uppercase tracking-wider">Time is of the essence</p>
          <p className="text-[11px] text-zinc-400 leading-relaxed">
            Every shot, every asset, every comment is timestamped. Autonomous bots relentlessly chase deadlines —
            alerting management before deadlines slip. The whole studio runs on a single clock.
          </p>
        </div>
        {(overdue + critical) > 0 && (
          <div className="text-right">
            <div className="text-2xl font-bold text-rose-400 tabular-nums leading-none">{overdue + critical}</div>
            <div className="text-[10px] text-rose-300 uppercase tracking-widest">at-risk now</div>
          </div>
        )}
      </div>

      {/* ── FORWARD LANE ── */}
      <Section title="Forward Lane · Artist → Client" icon={<ArrowRight className="h-4 w-4 text-emerald-400"/>} color="emerald">
        <Highway dir="forward" lanes={lanes.forward} />
      </Section>

      {/* ── REVERSE LANE ── */}
      <Section title="Reverse Lane · Client feedback cascade" icon={<ArrowLeft className="h-4 w-4 text-rose-400"/>} color="rose">
        <ReverseHighway reverse={lanes.reverse} />
      </Section>

      {/* ── DEADLINE RAIL ── */}
      <Section title="Deadline Rail · Every active task bucketed by hours-to-deadline" icon={<Clock className="h-4 w-4 text-amber-400"/>} color="amber">
        <DeadlineRail deadlines={deadlines} total={totalDeadline} />
      </Section>

      {/* ── BOT ORBIT + DATA/ASSET CORE ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Section title="Autonomous Bot Orbit · relentless watchdogs" icon={<Bot className="h-4 w-4 text-cyan-400"/>} color="cyan">
            <BotOrbit bots={bots} />
          </Section>
        </div>
        <Section title="Data + Asset Core" icon={<Database className="h-4 w-4 text-violet-400"/>} color="violet">
          <DataAssetCore />
        </Section>
      </div>

      {/* ── COMMUNICATION LEDGER ── */}
      <Section title="Communication Ledger · every event timestamped" icon={<MessageSquare className="h-4 w-4 text-sky-400"/>} color="sky">
        <Ledger ledger={ledger} />
      </Section>
    </div>
  );
}

/* ───────────────────── building blocks ──────────────────── */

function Section({ title, icon, color, children }) {
  const colorMap = { emerald: 'border-emerald-500/30', rose: 'border-rose-500/30', amber: 'border-amber-500/30', cyan: 'border-cyan-500/30', violet: 'border-violet-500/30', sky: 'border-sky-500/30' };
  return (
    <div className={`rounded-xl border bg-zinc-900/40 ${colorMap[color] || 'border-zinc-800'} p-4`}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h2 className="text-sm font-bold text-zinc-100">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Pill({ icon, label, value, color = 'zinc' }) {
  const map = { violet: 'text-violet-300 bg-violet-500/10 border-violet-500/30', amber: 'text-amber-300 bg-amber-500/10 border-amber-500/30', emerald: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30', zinc: 'text-zinc-300 bg-zinc-800 border-zinc-700' };
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-[11px] ${map[color]}`}>
      {icon}<span className="uppercase tracking-wider">{label}</span>
      <span className="text-sm font-bold tabular-nums">{value}</span>
    </div>
  );
}

function Highway({ dir, lanes }) {
  const stations = [
    { key: 'artist',     label: 'Artist',      icon: Upload,        color: '#fbbf24' },
    { key: 'supervisor_review', label: 'Supervisor / CD', icon: Shield, color: '#10b981' },
    { key: 'pm_review',  label: 'Producer',    icon: Send,          color: '#06b6d4' },
    { key: 'client_review', label: 'Client',   icon: Users,         color: '#a78bfa' },
  ];
  // map counts onto stations
  const countOf = (key) => lanes.find(l => l.stage === key)?.count ?? 0;
  const oldestOf = (key) => lanes.find(l => l.stage === key)?.oldest_age_h;
  const breachOf = (key) => lanes.find(l => l.stage === key)?.deadline_breach;

  return (
    <div className="relative">
      <div className="flex items-stretch justify-between gap-2 overflow-x-auto pb-2">
        {stations.map((s, i) => {
          const Icon = s.icon;
          const count = countOf(s.key);
          const breach = breachOf(s.key);
          const oldest = oldestOf(s.key);
          const isStart = i === 0;
          return (
            <React.Fragment key={s.key}>
              <div className="flex-1 min-w-[160px] relative" data-testid={`station-${s.key}`}>
                {/* Station card */}
                <div className={`relative rounded-xl border p-4 transition-all ${breach ? 'border-rose-500/60 bg-rose-500/5 shadow-[0_0_30px_rgba(244,63,94,0.25)]' : 'border-zinc-700 bg-zinc-900/70 hover:bg-zinc-900'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: `${s.color}1F`, border: `1px solid ${s.color}66` }}>
                      <Icon className="h-4 w-4" style={{ color: s.color }} />
                    </div>
                    <div className="font-semibold text-sm">{s.label}</div>
                  </div>
                  {!isStart ? (
                    <>
                      <div className="text-3xl font-bold tabular-nums" style={{ color: count > 0 ? s.color : '#52525b' }}>{count}</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">awaiting decision</div>
                      {oldest != null && (
                        <div className={`mt-1.5 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-mono ${breach ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40 animate-pulse' : 'bg-zinc-800 text-zinc-400'}`}>
                          <Clock className="h-2.5 w-2.5" />
                          oldest {oldest}h
                          {breach && <span className="font-bold">· BREACH</span>}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-[10px] text-zinc-500">Origin · uploads pushed via <span className="text-amber-300 font-semibold">/submit</span></div>
                  )}
                </div>
              </div>
              {i < stations.length - 1 && (
                <div className="flex flex-col justify-center items-center px-1 pt-6 shrink-0">
                  <div className={`w-10 h-0.5 ${dir === 'forward' ? 'bg-gradient-to-r from-amber-500 via-emerald-500 to-cyan-500' : 'bg-gradient-to-l from-rose-500 via-rose-400 to-rose-300'} relative`}>
                    <div className={`absolute -top-[3px] ${dir === 'forward' ? 'right-0' : 'left-0'} w-0 h-0 border-y-[4px] border-y-transparent ${dir === 'forward' ? 'border-l-[6px] border-l-cyan-400' : 'border-r-[6px] border-r-rose-400'}`} />
                  </div>
                  <div className="text-[9px] text-zinc-600 mt-1 uppercase tracking-widest">{dir === 'forward' ? 'forward' : 'reverse'}</div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      {dir === 'forward' && (
        <div className="mt-2 flex items-center gap-3 text-[10px] text-zinc-500">
          <Sparkles className="h-3 w-3 text-amber-400"/>
          <span>Each arrow = a Resend email + sidebar badge bump. Crossing each station <strong className="text-amber-300">costs hours</strong> — measured by the "oldest" badge on each card.</span>
        </div>
      )}
    </div>
  );
}

function ReverseHighway({ reverse }) {
  const breached = (reverse.oldest_age_h || 0) > 24;
  const stations = [
    { label: 'Client',      icon: Users,    color: '#a78bfa' },
    { label: 'Producer',    icon: Send,     color: '#06b6d4' },
    { label: 'Supervisor',  icon: Shield,   color: '#10b981' },
    { label: 'Artist',      icon: Upload,   color: '#fbbf24' },
  ];
  return (
    <div className="relative">
      <div className="flex items-stretch justify-between gap-2">
        {stations.map((s, i) => {
          const Icon = s.icon;
          return (
            <React.Fragment key={s.label}>
              <div className={`flex-1 min-w-[140px] rounded-xl border p-3 ${i === 0 && reverse.count > 0 ? 'border-rose-500/60 bg-rose-500/10 shadow-[0_0_30px_rgba(244,63,94,0.3)] animate-pulse' : 'border-zinc-700 bg-zinc-900/60'}`} data-testid={`reverse-station-${s.label}`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="h-7 w-7 rounded-lg flex items-center justify-center" style={{ background: `${s.color}1F`, border: `1px solid ${s.color}66` }}>
                    <Icon className="h-4 w-4" style={{ color: s.color }} />
                  </div>
                  <div className="font-semibold text-xs">{s.label}</div>
                </div>
                {i === 0 ? (
                  <>
                    <div className="text-2xl font-bold tabular-nums text-rose-400">{reverse.count}</div>
                    <div className="text-[9px] text-rose-300 uppercase tracking-widest">in revise</div>
                    {reverse.oldest_age_h != null && (
                      <div className={`mt-1.5 inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded font-mono ${breached ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40' : 'bg-zinc-800 text-zinc-400'}`}>
                        <Clock className="h-2.5 w-2.5" />stale {reverse.oldest_age_h}h
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-[9px] text-zinc-500">Receives cascade email + badge</div>
                )}
              </div>
              {i < stations.length - 1 && (
                <div className="flex flex-col justify-center items-center px-1 shrink-0">
                  <div className="w-8 h-0.5 bg-gradient-to-l from-rose-300 via-rose-400 to-rose-500 relative">
                    <div className="absolute -top-[3px] left-0 w-0 h-0 border-y-[4px] border-y-transparent border-r-[6px] border-r-rose-400"/>
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      <div className="mt-2 text-[10px] text-zinc-500 flex items-start gap-1">
        <Zap className="h-3 w-3 text-rose-400 mt-0.5"/>
        <span>One client revise → <strong className="text-rose-300">single email batch</strong> hits Artist + Supervisor + Creative Director + Producer + Admin simultaneously. Nobody hides from feedback.</span>
      </div>
    </div>
  );
}

function DeadlineRail({ deadlines, total }) {
  if (total === 0) {
    return <div className="text-xs text-zinc-500 text-center py-6">No active tasks have a deadline set yet. Add deadlines from the Allotment Engine.</div>;
  }
  return (
    <>
      <div className="flex h-8 rounded-lg overflow-hidden border border-zinc-700" data-testid="deadline-rail">
        {deadlines.map(d => {
          const pct = (d.count / total) * 100;
          if (pct === 0) return null;
          return (
            <div key={d.bucket} className="flex items-center justify-center text-[10px] font-bold text-zinc-900 transition-all hover:brightness-125"
              style={{ width: `${pct}%`, background: d.color }}
              title={`${d.label}: ${d.count} task${d.count !== 1 ? 's' : ''}`}
              data-testid={`deadline-bucket-${d.bucket}`}>
              {pct > 8 && d.count}
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-5 gap-2 mt-3">
        {deadlines.map(d => (
          <div key={d.bucket} className="rounded border border-zinc-800 bg-zinc-900/50 p-2 text-center">
            <div className="h-1 rounded mb-1.5" style={{ background: d.color }}/>
            <div className="text-2xl font-bold tabular-nums" style={{ color: d.color }}>{d.count}</div>
            <div className="text-[9px] text-zinc-400 uppercase tracking-wider">{d.label}</div>
          </div>
        ))}
      </div>
    </>
  );
}

function BotOrbit({ bots }) {
  const sevColor = { critical: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#84cc16' };
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {bots.map(b => {
          const c = sevColor[b.severity] || '#64748b';
          const next = b.next_in_min;
          return (
            <div key={b.code} className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-2.5 hover:bg-zinc-900 transition-colors" data-testid={`bot-orbit-${b.code}`}>
              <div className="flex items-start gap-2">
                <div className="relative shrink-0 mt-0.5">
                  <span className="block h-2 w-2 rounded-full" style={{ background: c }}/>
                  {b.active && <span className="absolute inset-0 h-2 w-2 rounded-full animate-ping" style={{ background: c, opacity: 0.6 }}/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono text-zinc-500">{b.code}</div>
                  <div className="text-[11px] font-semibold text-zinc-100 leading-tight truncate">{b.name}</div>
                  <div className="flex items-center justify-between mt-1 text-[9px] text-zinc-500">
                    <span>every {b.schedule_minutes}m</span>
                    {b.active && next != null && <span className="text-cyan-300 font-mono">next {next}m</span>}
                  </div>
                  {b.open_alerts > 0 && (
                    <div className="mt-1 text-[9px] text-rose-300 font-bold">⚠ {b.open_alerts} open alert{b.open_alerts !== 1 ? 's' : ''}</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 text-[10px] text-zinc-500 flex items-start gap-1">
        <CircleDot className="h-3 w-3 text-cyan-400 mt-0.5"/>
        <span>Scheduler ticks every <strong className="text-cyan-300">30 seconds</strong>. Each bot fires on its own schedule, dedupes alerts, escalates by severity, and emails on critical/high.</span>
      </div>
    </div>
  );
}

function DataAssetCore() {
  return (
    <div className="space-y-3 text-xs">
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
        <div className="flex items-center gap-2 mb-1.5"><Database className="h-3.5 w-3.5 text-violet-400"/><span className="font-semibold text-violet-200">Data plane (MongoDB)</span></div>
        <p className="text-[10px] text-zinc-400 leading-relaxed">users · projects · project_access · review_submissions · review_annotations · tasks · mdl_items · shot_registry · bots_alerts · notifications_log</p>
      </div>
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
        <div className="flex items-center gap-2 mb-1.5"><HardDrive className="h-3.5 w-3.5 text-amber-400"/><span className="font-semibold text-amber-200">Asset plane (NAS + Object Storage)</span></div>
        <ul className="text-[10px] text-zinc-400 leading-relaxed space-y-1">
          <li><strong className="text-zinc-300">QNAP NAS</strong> — raw .blend, .ma, .psd, .exr (master) preserved untouched</li>
          <li><strong className="text-zinc-300">Object Storage</strong> — H.264 web proxies (−90% size), thumbnails, instant scrub</li>
          <li><strong className="text-zinc-300">Browser canvas</strong> — annotation thumbs rendered live, never persisted</li>
        </ul>
      </div>
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
        <div className="flex items-center gap-2 mb-1.5"><Zap className="h-3.5 w-3.5 text-emerald-400"/><span className="font-semibold text-emerald-200">Notification plane</span></div>
        <p className="text-[10px] text-zinc-400 leading-relaxed">Resend API · DMARC-aligned vawworld.in · welcome / submit / decision / digest / bot alert templates · all events logged with UTC ISO timestamps.</p>
      </div>
    </div>
  );
}

function Ledger({ ledger }) {
  if (!ledger?.length) return <div className="text-xs text-zinc-500 text-center py-6">No events yet.</div>;
  const kindColor = (k) => ({
    supervisor_review: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
    pm_review:         'text-cyan-300 bg-cyan-500/10 border-cyan-500/30',
    client_review:     'text-violet-300 bg-violet-500/10 border-violet-500/30',
    final_approved:    'text-emerald-200 bg-emerald-500/15 border-emerald-500/40',
    client_revise:     'text-rose-300 bg-rose-500/10 border-rose-500/30',
    client_rejected:   'text-rose-200 bg-rose-500/15 border-rose-500/40',
    supervisor_revise: 'text-amber-300 bg-amber-500/10 border-amber-500/30',
    pm_revise:         'text-amber-300 bg-amber-500/10 border-amber-500/30',
  })[k] || 'text-zinc-300 bg-zinc-800 border-zinc-700';
  return (
    <ol className="space-y-1.5 max-h-[420px] overflow-y-auto" data-testid="comm-ledger">
      {ledger.map((e, i) => (
        <li key={i} className="flex items-center gap-3 px-3 py-2 rounded border border-zinc-800 bg-zinc-900/60 hover:bg-zinc-900" data-testid={`ledger-row-${i}`}>
          <span className="font-mono text-[10px] text-zinc-500 w-32 shrink-0">{fmtTs(e.ts)}</span>
          <span className={`text-[9px] uppercase tracking-wider font-semibold rounded px-1.5 py-0.5 border ${kindColor(e.kind)}`}>{(e.kind || '').replace(/_/g, ' ')}</span>
          <span className="text-[11px] text-zinc-300 truncate flex-1 min-w-0">{e.target}</span>
          <span className="text-[10px] text-zinc-500">R{e.round} · {(e.stage || '').toUpperCase()}</span>
        </li>
      ))}
    </ol>
  );
}

function fmtTs(ts) {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return ts; }
}
